import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { LanguageCode } from '@sparted/shared-library/business/types';

import { fetchAudienceMetadata } from 'Features/audience/stores/thunks/audience-group.thunks';
import { inferInitialSelectedLanguage } from 'Services/language/routing';

import { fromApiCampaignMapper } from './activity.mapper';
import { activityAdapter } from './activity.selectors';
import {
  archiveActivity,
  fetchBreakingNewsById,
  fetchCampaignById,
  requestCampaignContentsTranslation,
  saveCampaign,
  searchActivities,
  unarchiveActivity,
  updateBreakingNews,
} from './activity.thunks';
import { ActivityEditionResponse, ActivityState } from './activity.type';
import { extractActivityApiError } from './libs/errorHandling';

const initialState = {
  search: {
    total: 0,
    loading: false,
    error: undefined,
  },
  edition: {
    saving: false,
    loading: false,
    error: null,
    success: null,
    campaign: undefined,
    breakingNews: undefined,
    locked: false,
    selectedLanguage: 'en-US' as const,
    dirty: false,
    numberTargetedPlayers: null,
  },
};

export const activityState: ActivityState = activityAdapter.getInitialState(initialState);

export const { actions: activityActions, reducer: activityReducer } = createSlice({
  name: 'activity',
  initialState: activityState,
  reducers: {
    resetEdition: (state: ActivityState) => ({ ...state, edition: initialState.edition }),
    resetSearch: (state: ActivityState) => ({ ...state, search: initialState.search }),
    setEditionLock: (state: ActivityState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      edition: { ...state.edition, locked: payload },
    }),
    setEditionLanguage: (state: ActivityState, { payload }: PayloadAction<LanguageCode>) => ({
      ...state,
      edition: { ...state.edition, selectedLanguage: payload },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(searchActivities.pending, (state, payload) => {
      const { offset } = payload.meta.arg;

      if (offset === 0) {
        activityAdapter.removeAll(state);
        state.search.total = 0;
      }

      state.search.loading = true;
      state.search.error = undefined;
    });

    builder.addCase(searchActivities.rejected, (state) => {
      state.search.loading = false;
      state.search.error = 'search-error';
    });

    builder.addCase(searchActivities.fulfilled, (state, payload) => {
      const { offset } = payload.meta.arg;
      const {
        items,
        metadata: { total },
      } = payload.payload;

      state.search.total = total;
      state.search.loading = false;

      if (offset === 0) {
        activityAdapter.setAll(state, items);
      } else {
        activityAdapter.addMany(state, items);
      }
    });

    builder.addCase(fetchCampaignById.rejected, (state: ActivityState) => {
      return { ...state, edition: { ...state.edition, loading: false } };
    });
    builder.addCase(fetchCampaignById.pending, (state: ActivityState) => {
      return { ...state, edition: { ...state.edition, loading: false } };
    });
    builder.addCase(fetchCampaignById.fulfilled, (state: ActivityState, { payload }) => {
      const campaign = fromApiCampaignMapper(payload);

      return {
        ...state,
        edition: {
          ...state.edition,
          loading: false,
          campaign,
          locked: campaign?.status === 'live' || campaign?.status === 'finished' || campaign?.status === 'upcoming',
          selectedLanguage: inferInitialSelectedLanguage(campaign?.languages || []),
        },
      };
    });

    builder.addCase(saveCampaign.pending, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: true,
        error: null,
        loading: false,
      },
    }));
    builder.addCase(saveCampaign.rejected, (state: ActivityState, { payload }) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: false,
        loading: false,
        error: extractActivityApiError(payload),
      },
    }));
    builder.addCase(saveCampaign.fulfilled, (state: ActivityState, { payload }) => {
      return {
        ...state,
        edition: {
          ...state.edition,
          campaign: fromApiCampaignMapper(payload),
          saving: false,
          dirty: false,
          loading: false,
          error: null,
        },
      };
    });

    // requestCampaignContentsTranslation
    builder.addCase(requestCampaignContentsTranslation.pending, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: true,
        error: null,
        loading: false,
      },
    }));
    builder.addCase(requestCampaignContentsTranslation.rejected, (state: ActivityState, { payload }) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: false,
        loading: false,
        error: extractActivityApiError(payload),
      },
    }));
    builder.addCase(requestCampaignContentsTranslation.fulfilled, (state: ActivityState, { payload }) => {
      if (!state.edition.campaign) {
        return state;
      }

      const updatedContentsMap = Object.fromEntries(payload.map((content) => [content.id, content]));

      let updatedRandomContents;
      let updatedManualContents;

      const mode = state.edition.campaign.scheduling.mode;

      if (mode === 'random') {
        const contents = state.edition.campaign.scheduling.contents || [];
        updatedRandomContents = contents.map((content) => ({
          ...content,
          statuses: updatedContentsMap[content.id]?.statuses || content.statuses,
        }));
      }

      if (mode === 'manual') {
        const sessions = state.edition.campaign.scheduling.orderedSessions || [];
        updatedManualContents = sessions.map((session) =>
          session.map((content) => ({
            ...content,
            statuses: updatedContentsMap[content.id]?.statuses || content.statuses,
          })),
        );
      }

      const updatedState = {
        ...state,
        edition: {
          ...state.edition,
          campaign: {
            ...state.edition.campaign,
            scheduling: {
              ...state.edition.campaign?.scheduling,
              orderedSessions: updatedManualContents || state.edition.campaign?.scheduling.orderedSessions,
              contents: updatedRandomContents || state.edition.campaign?.scheduling.contents,
            },
          },
          saving: false,
          dirty: false,
          loading: false,
          error: null,
        },
      };

      return updatedState;
    });

    builder.addCase(fetchBreakingNewsById.rejected, (state: ActivityState) => {
      return { ...state, edition: { ...state.edition, loading: false } };
    });

    builder.addCase(fetchBreakingNewsById.pending, (state: ActivityState) => {
      return { ...state, edition: { ...state.edition, loading: false } };
    });

    builder.addCase(fetchBreakingNewsById.fulfilled, (state: ActivityState, { payload }) => {
      const breakingNews = payload;
      return {
        ...state,
        edition: {
          ...state.edition,
          loading: false,
          breakingNews,
          locked: breakingNews?.status === 'live',
          selectedLanguage: inferInitialSelectedLanguage(breakingNews?.languages || []),
        },
      };
    });

    builder.addCase(fetchAudienceMetadata.fulfilled, (state: ActivityState, { payload }) => {
      return {
        ...state,
        edition: {
          ...state.edition,
          numberTargetedPlayers: payload.numberOfPlayers,
        },
      };
    });

    builder.addCase(updateBreakingNews.pending, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: true,
        error: null,
        loading: false,
      },
    }));

    builder.addCase(updateBreakingNews.rejected, (state: ActivityState, { payload }) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: false,
        error: extractActivityApiError(payload),
        loading: false,
      },
    }));

    builder.addCase(updateBreakingNews.fulfilled, (state: ActivityState, { payload }) => {
      return {
        ...state,
        edition: {
          ...state.edition,
          breakingNews: payload,
          saving: false,
          dirty: false,
          loading: false,
          error: null,
        },
      };
    });

    // Archive activity
    builder.addCase(archiveActivity.pending, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: true,
        success: null,
        error: null,
        loading: false,
      },
    }));

    builder.addCase(archiveActivity.rejected, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: false,
        success: null,
        error: ActivityEditionResponse.ARCHIVE_ERROR,
        loading: false,
      },
    }));

    builder.addCase(archiveActivity.fulfilled, (state: ActivityState, { payload }) => {
      activityAdapter.updateOne(state, { id: payload.id, changes: { status: 'archived' } });

      if (state.edition.campaign) {
        state.edition.campaign.status = 'archived';
      }

      if (state.edition.breakingNews) {
        state.edition.breakingNews.status = 'archived';
      }

      state.edition.saving = false;
      state.edition.dirty = false;
      state.edition.loading = false;
      state.edition.error = null;
      state.edition.success = ActivityEditionResponse.ARCHIVE_SUCCESS;
      return state;
    });

    // Unarchive activity
    builder.addCase(unarchiveActivity.pending, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: true,
        error: null,
        success: null,
        loading: false,
      },
    }));

    builder.addCase(unarchiveActivity.rejected, (state: ActivityState) => ({
      ...state,
      edition: {
        ...state.edition,
        saving: false,
        error: ActivityEditionResponse.UNARCHIVE_ERROR,
        success: null,
        loading: false,
      },
    }));

    builder.addCase(unarchiveActivity.fulfilled, (state: ActivityState, { payload }) => {
      activityAdapter.updateOne(state, { id: payload.id, changes: { status: 'draft' } });

      if (state.edition.campaign) {
        state.edition.campaign.status = 'draft';
      }

      if (state.edition.breakingNews) {
        state.edition.breakingNews.status = 'draft';
      }

      state.edition.saving = false;
      state.edition.dirty = false;
      state.edition.loading = false;
      state.edition.error = null;
      state.edition.success = ActivityEditionResponse.UNARCHIVE_SUCCESS;
      return state;
    });
  },
});
